import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchResult } from '../api';
import ResultsRadar from '../components/ResultsRadar';
import useNoIndex from '../hooks/useNoIndex';

function pickQuickWin(dimensionKey, answers, snapshot) {
  const questionsInDim = snapshot.questions.filter(
    (q) => q.dimensionKey === dimensionKey && snapshot.quickWins?.[q.id]
  );
  if (questionsInDim.length === 0) return null;
  const entries = questionsInDim.map((q) => {
    const maxVal = Math.max(...q.options.map((o) => o.value));
    const s = typeof answers[q.id] === 'number' ? answers[q.id] : maxVal;
    return { qid: q.id, score: s };
  });
  entries.sort((a, b) => a.score - b.score);
  const pick = entries[0];
  return { dimensionKey, questionId: pick.qid, copy: snapshot.quickWins[pick.qid] };
}

export default function Results() {
  useNoIndex();
  const { token } = useParams();
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchResult(token)
      .then(setResult)
      .catch((err) => setError(err?.response?.data?.error || 'Could not load result.'));
  }, [token]);

  if (error) {
    return <div className="page"><div className="error">{error}</div></div>;
  }
  if (!result) {
    return <div className="page"><p className="muted">Loading your results…</p></div>;
  }

  const snapshot = result.questionnaireSnapshot;
  const dimensionLabel = (key) => snapshot.dimensions.find((d) => d.key === key)?.label || key;

  const quickWins = result.topGaps.slice(0, 2)
    .map((dimKey) => pickQuickWin(dimKey, result.answers || {}, snapshot))
    .filter(Boolean);

  return (
    <div className="page page--wide">
      <div className="card">
        <div className="score-block">
          <p className="muted small" style={{ marginBottom: '0.5rem' }}>Your AI Readiness Score</p>
          <div className="score-number">{result.score}</div>
          <div className="score-tier">{result.tier}</div>
          <p className="score-tagline">{result.tierDescription}</p>
          <button
            type="button"
            className="secondary no-print"
            style={{ marginTop: '1rem' }}
            onClick={() => window.print()}
          >
            Download results (PDF)
          </button>
        </div>
      </div>

      <div className="results-section results-section--featured">
        <p className="section-eyebrow">Where to focus</p>
        <h2>Your three biggest opportunities</h2>
        <p className="section-lede">
          The dimensions where the gap between where you are and where you could be is widest.
        </p>
        <ul className="gap-list">
          {result.topGaps.map((key) => (
            <li key={key} className="gap-item">
              <strong>{dimensionLabel(key)}</strong>
              <span className="muted small">
                You scored {result.dimensions[key].percent}% on this dimension.
              </span>
            </li>
          ))}
        </ul>
      </div>

      {quickWins.length > 0 && (
        <div className="results-section results-section--featured">
          <p className="section-eyebrow">Start here</p>
          <h2>Two things to do this week</h2>
          <p className="section-lede">
            Concrete, low-cost moves targeted at your two weakest areas. Each one takes under an hour.
          </p>
          <div className="quickwin-list">
            {quickWins.map((win, idx) => (
              <div key={win.dimensionKey} className="quickwin-card">
                <div className="quickwin-number">{idx + 1}</div>
                <div className="quickwin-body">
                  <p>{win.copy}</p>
                  <p className="quickwin-focus">
                    Focused on: <strong>{dimensionLabel(win.dimensionKey)}</strong>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="results-section results-section--supporting">
        <h2>How you scored, dimension by dimension</h2>
        <p className="muted small">
          The dotted line is a rough &ldquo;typical SMB&rdquo; benchmark. The further your shape sits inside it,
          the bigger the opportunity.
        </p>
        <div className="card">
          <ResultsRadar
            dimensionDefs={snapshot.dimensions}
            dimensions={result.dimensions}
            benchmarkPercent={snapshot.benchmarkPercent}
          />
        </div>
      </div>

      <div className="cta cta--personal">
        <img className="cta-portrait" src="/images/alex-headshot.jpg" alt="Alex Wingert" />
        <div className="cta-body">
          <h3>Want help turning this into a 90-day roadmap?</h3>
          <p>
            I&rsquo;m Alex — I run a flat-fee engagement that audits your software stack,
            evaluates your vendors, and builds you a prioritized technology roadmap.
            Most clients land 25&ndash;45 on this scorecard and walk away with a concrete plan.
          </p>
          <a href="https://calendar.app.google/CDJUpjf8rsp9kqjR6" target="_blank" rel="noopener noreferrer">Book a call with Alex</a>
        </div>
      </div>

      <p className="muted small center no-print" style={{ marginTop: '3rem' }}>
        This link is yours — bookmark it to come back any time, or download a PDF copy above.
      </p>
    </div>
  );
}
